import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  VaAlert,
  VaSelect,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { withRouter } from 'react-router';

import { apiRequest } from '@department-of-veterans-affairs/platform-utilities/api';
import { focusElement } from 'platform/utilities/ui';
import FormNavButtons from '~/platform/forms-system/src/js/components/FormNavButtons';

import { clearFormData, removeAskVaForm } from '../actions';
import Loader from '../components/Loader';
import SignInMayBeRequiredCategoryPage from '../components/SignInMayBeRequiredCategoryPage';
import { ServerErrorAlert } from '../config/helpers';
import {
  URL,
  USER_ADOPTION,
  getApiUrl,
  hasPrefillInformation,
} from '../constants';
import { askVAAttachmentStorage } from '../utils/StorageAdapter';

const CategorySelectPage = props => {
  const {
    onChange,
    isLoggedIn,
    formData,
    goBack,
    goForward,
    router,
    formId,
    contentBeforeButtons,
    contentAfterButtons,
  } = props;
  const dispatch = useDispatch();
  const [apiData, setApiData] = useState([]);
  const [loading, isLoading] = useState(false);
  const [error, hasError] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const isLOA3 = useSelector(state => state.user.profile.loa.current === 3);

  const handleContinue = () => {
    if (formData.selectCategory) {
      return goForward(formData);
    }

    setValidationError('Select a category');
    const select = document.querySelector('va-select#root_selectCategory');
    if (select?.shadowRoot) {
      return focusElement('select', {}, select?.shadowRoot);
    }

    return focusElement('va-select#root_selectCategory');
  };

  const handleChange = event => {
    const selectedValue = event.detail.value;

    if (!selectedValue || selectedValue === '') {
      onChange({
        ...formData,
        categoryId: null,
        selectCategory: '',
        categoryAllowAttachments: false,
        categoryRequiresSignIn: false,
        requireSignInLogic: {
          ...formData.requireSignInLogic,
          category: false,
        },
        contactPreferences: [],
      });
      return;
    }

    const selected = apiData.find(
      category => category.attributes.name === selectedValue,
    );

    if (!selected) return;

    (async () => {
      await askVAAttachmentStorage.clear();
    })();

    const initialData =
      formData.initialFormData === undefined
        ? { ...formData }
        : { ...formData.initialFormData };

    onChange({
      ...formData,
      hasPrefillInformation: hasPrefillInformation(formData),
      // FOR TESTING PREFILL LOCALLY
      // hasPrefillInformation: true,
      // aboutYourself: {
      //   first: 'Wallace',
      //   middle: 'R',
      //   last: 'Webb',
      //   socialOrServiceNum: {
      //     ssn: '796128064',
      //   },
      //   dateOfBirth: '1950-09-13',
      // },
      // schoolInfo: {
      //   schoolFacilityCode: '31002144',
      //   schoolName: 'WESTERN GOVERNORS UNIVERSITY',
      // },
      // businessPhone: '333444555556',
      // businessEmail: 'test@business.email',
      /// /
      initialFormData: initialData,
      categoryId: selected.id,
      selectCategory: selectedValue,
      categoryAllowAttachments: selected.attributes.allowAttachments,
      contactPreferences: selected.attributes.contactPreferences,
      categoryRequiresSignIn:
        selected.attributes.requiresAuthentication && !isLOA3,
      requireSignInLogic: formData.requireSignInLogic
        ? {
            ...formData.requireSignInLogic,
            category: selected.attributes.requiresAuthentication,
          }
        : {
            category: selected.attributes.requiresAuthentication,
            topic: false,
            personQuestionIsAbout: false,
          },
    });
  };

  const getApiData = url => {
    isLoading(true);
    return apiRequest(url)
      .then(res => {
        setApiData(res.data);
        isLoading(false);
      })
      .catch(() => {
        isLoading(false);
        hasError(true);
      });
  };

  useEffect(() => {
    getApiData(getApiUrl(URL.GET_CATEGORIES));
  }, [isLoggedIn]);

  useEffect(() => {
    focusElement('h2');
  }, [loading]);

  const handleGoBack = () => {
    if (!hasPrefillInformation(formData)) {
      dispatch(clearFormData());
      dispatch(removeAskVaForm(formId));
      router.push('/');
    } else {
      goBack();
    }
  };

  if (loading) return <Loader />;

  // ask_va_predictive_category - only for the AI-prediction-failed path, not a manual "None of these" decline
  const showPredictionErrorAlert =
    formData.toggle_ask_va_predictive_category === true &&
    formData.predictions?.category?.userAdoption === USER_ADOPTION.ERROR;

  return !error ? (
    <>
      <SignInMayBeRequiredCategoryPage />
      {showPredictionErrorAlert && (
        <VaAlert status="warning" className="vads-u-margin-y--3">
          <h2 slot="headline">We’re having trouble suggesting a category</h2>
          <p className="vads-u-margin-y--0">
            We’re sorry. Something went wrong in our system. You can try loading
            suggestions again, or pick a category yourself. We saved your
            question, so you don’t need to retype it.
          </p>
          <va-link
            href="#"
            onClick={e => {
              e.preventDefault();
              handleGoBack();
            }}
            text="Go back and load suggestions again"
          />
        </VaAlert>
      )}
      <form className="rjsf" data-dd-privacy="allow">
        <VaSelect
          id="root_selectCategory"
          label="Select the category that best describes your question:"
          label-header-level={3}
          name="root_selectCategory"
          value={formData.selectCategory}
          onVaSelect={handleChange}
          error={validationError}
          required
          uswds
        >
          {apiData.map(category => (
            <option
              key={category.id}
              value={category.attributes.name}
              id={category.id}
            >
              {category.attributes.displayName}
            </option>
          ))}
        </VaSelect>

        {contentBeforeButtons}
        <FormNavButtons goBack={handleGoBack} goForward={handleContinue} />
        {contentAfterButtons}
      </form>
    </>
  ) : (
    <ServerErrorAlert />
  );
};

CategorySelectPage.propTypes = {
  contentAfterButtons: PropTypes.node,
  contentBeforeButtons: PropTypes.node,
  formData: PropTypes.object,
  formId: PropTypes.string,
  goBack: PropTypes.func,
  goForward: PropTypes.func,
  goToPath: PropTypes.func,
  id: PropTypes.string,
  isLoggedIn: PropTypes.bool,
  router: PropTypes.object,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    isLoggedIn: state.user.login.currentlyLoggedIn,
    formData: state.form.data,
    formId: state.form.formId,
  };
}

export default connect(mapStateToProps)(withRouter(CategorySelectPage));
